import React, { useState } from "react";
import { ImPlus, ImMinus } from "react-icons/im";
import NavTitle from "./NavTitle";
import { motion } from "framer-motion";

/**
 * SEO-Friendly Trade Categories Component
 * Allows users to browse product categories with expandable subcategories
 * Enhanced for accessibility, structured data, and keyword optimization
 */
const Category = () => {
  const [showAgriculture, setShowAgriculture] = useState(false);
  const [showAccessories, setShowAccessories] = useState(false);

  const categories = [
    {
      _id: 990,
      title: "Agriculture Products",
      icons: true,
      keywords: "fresh vegetables, green chili, red chili, onions, cucumber",
    },
    {
      _id: 994,
      title: "Other Products",
      icons: false,
      keywords: "exported goods, specialty items, high-quality products",
    },
  ];

  const agricultureProducts = [
    { _id: 1, title: "Green Chilli", base: "#22c55e" },
    { _id: 2, title: "Red Chilli", base: "#dc2626" },
    { _id: 3, title: "Onion", base: "#f87171" },
    { _id: 4, title: "Red Rose Onion", base: "#ef4444" },
    { _id: 5, title: "Cucumber", base: "#22c55e" },
  ];

  const handleToggle = (categoryTitle) => {
    if (categoryTitle === "Agriculture Products") {
      setShowAgriculture(!showAgriculture);
    } else if (categoryTitle === "Accessories") {
      setShowAccessories(!showAccessories);
    }
  };

  return (
    <aside className="w-full">
      <NavTitle title="Trade by Product Category" icons={false} />
      <ul className="flex flex-col gap-4 text-sm lg:text-base text-gray-700">
        {categories.map(({ _id, title, icons, keywords }) => (
          <li
            key={_id}
            className="border-b-[1px] border-gray-200 pb-2"
            aria-label={`Category: ${title}`}
          >
            <div className="flex justify-between items-center cursor-pointer">
              <h3 className="text-gray-800 font-medium">{title}</h3>
              {icons && (
                <button
                  onClick={() => handleToggle(title)}
                  aria-label={`Toggle ${title} Subcategories`}
                  className="text-sm text-gray-800 hover:text-primeColor transition duration-300"
                >
                  {(title === "Agriculture Products" && !showAgriculture) ||
                  (title === "Accessories" && !showAccessories) ? (
                    <ImPlus />
                  ) : (
                    <ImMinus />
                  )}
                </button>
              )}
            </div>

            {/* Agriculture Subcategories */}
            {title === "Agriculture Products" && showAgriculture && (
              <motion.ul
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="flex flex-col gap-2 mt-2 text-gray-800"
              >
                {agricultureProducts.map((item) => (
                  <li
                    key={item._id}
                    className="flex items-center gap-2 pb-1 border-b border-gray-100"
                  >
                    <span
                      style={{ background: item.base }}
                      className="w-3 h-3 rounded-full"
                    ></span>
                    <span>{item.title}</span>
                  </li>
                ))}
              </motion.ul>
            )}

            {/* Accessories Placeholder */}
            {title === "Accessories" && showAccessories && (
              <motion.div
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <p className="text-gray-600 text-sm italic">
                  Accessories subcategories will be added soon.
                </p>
              </motion.div>
            )}

            {/* Hidden SEO Keywords for Search Engines */}
            <meta name="keywords" content={keywords || ""} />
          </li>
        ))}
      </ul>
    </aside>
  );
};

export default Category;
